import { apiService as api } from './api';
import { Veiculo, Motorista, Abastecimento, Manutencao, Despesa, Viagem, ApiResponse, Paginacao } from '../types';

export interface VeiculosFilters {
  // Filtro por catalogo, e nao por texto: o `like` antigo casava
  // "Ford" com "Fordson" e nao casava "Volkswagen" com "VolksWagen".
  marca_id?: number | string;
  modelo_id?: number | string;
  status?: string;
  categoria?: string;
  placa?: string;
  sort_by?: string;
  sort_order?: string;
  per_page?: number;
  page?: number;
}

export interface VeiculosResponse {
  success: boolean;
  data: Veiculo[];
  message?: string;
  pagination: Paginacao;
}

export interface VeiculoResponse {
  success: boolean;
  data: Veiculo;
  message?: string;
  estatisticas?: any;
}

export interface DeleteResponse {
  success: boolean;
  message: string;
}

export const veiculosService = {
  // Listar veículos com filtros
  getVeiculos: async (filters: VeiculosFilters = {}): Promise<VeiculosResponse> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get<VeiculosResponse>(`/v1/veiculos?${params.toString()}`);
    return response;
  },

  // Buscar veículo por ID
  getVeiculo: async (id: number): Promise<VeiculoResponse> => {
    const response = await api.get<VeiculoResponse>(`/v1/veiculos/${id}`);
    return response;
  },

  // Criar novo veículo
  createVeiculo: async (veiculo: Partial<Veiculo>): Promise<VeiculoResponse> => {
    const response = await api.post<VeiculoResponse>('/v1/veiculos', veiculo);
    return response;
  },

  // Atualizar veículo
  updateVeiculo: async (id: number, veiculo: Partial<Veiculo>): Promise<VeiculoResponse> => {
    const response = await api.put<VeiculoResponse>(`/v1/veiculos/${id}`, veiculo);
    return response;
  },

  // Excluir veículo
  deleteVeiculo: async (id: number): Promise<DeleteResponse> => {
    const response = await api.delete<DeleteResponse>(`/v1/veiculos/${id}`);
    return response;
  },

  // Listar motoristas do veículo
  getMotoristas: async (veiculoId: number, page: number = 1): Promise<ApiResponse<Motorista[]>> => {
    const response = await api.get<ApiResponse<Motorista[]>>(`/v1/veiculos/${veiculoId}/motoristas?page=${page}`);
    return response;
  },

  // Atribuir motorista ao veículo
  atribuirMotorista: async (veiculoId: number, motoristaId: number, observacoes?: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/v1/veiculos/${veiculoId}/motoristas/${motoristaId}`, {
      observacoes
    });
    return response;
  },

  // Remover motorista do veículo
  removerMotorista: async (veiculoId: number, motoristaId: number): Promise<DeleteResponse> => {
    const response = await api.delete<DeleteResponse>(`/v1/veiculos/${veiculoId}/motoristas/${motoristaId}`);
    return response;
  },

  // Listar abastecimentos do veículo
  getAbastecimentos: async (veiculoId: number, filters: any = {}): Promise<ApiResponse<Abastecimento[]>> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get<ApiResponse<Abastecimento[]>>(`/v1/veiculos/${veiculoId}/abastecimentos?${params.toString()}`);
    return response;
  },

  // Listar manutenções do veículo
  getManutencoes: async (veiculoId: number, filters: any = {}): Promise<ApiResponse<Manutencao[]>> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get<ApiResponse<Manutencao[]>>(`/v1/veiculos/${veiculoId}/manutencoes?${params.toString()}`);
    return response;
  },

  // Listar despesas do veículo
  getDespesas: async (veiculoId: number, filters: any = {}): Promise<ApiResponse<Despesa[]>> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get<ApiResponse<Despesa[]>>(`/v1/veiculos/${veiculoId}/despesas?${params.toString()}`);
    return response;
  },

  // Listar viagens do veículo
  getViagens: async (veiculoId: number, filters: any = {}): Promise<ApiResponse<Viagem[]>> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get<ApiResponse<Viagem[]>>(`/v1/veiculos/${veiculoId}/viagens?${params.toString()}`);
    return response;
  },

  // Calcular consumo do veículo
  getConsumo: async (veiculoId: number): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/v1/veiculos/${veiculoId}/consumo`);
    return response;
  },

  // Status dos documentos do veículo
  getStatusDocumentos: async (veiculoId: number): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/v1/veiculos/${veiculoId}/documentos/status`);
    return response;
  },
};
